import { Box, BoxProps, Flex, forwardRef } from '@chakra-ui/react'
import React, {
  ForwardedRef,
  MutableRefObject,
  RefCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { ComponentType } from 'react'

export interface ScrollbarOptions {
  hidden?: boolean
  showOnHoverOnly?: boolean
}

export type ScrollAreaProps = BoxProps & {
  scrollbarOptions?: ScrollbarOptions
  scrollTop?: number
}

type UseCustomScrollerProps = ScrollAreaProps & {
  deps?: unknown[]
}
export const useScrollingListScrollArea = (props: UseCustomScrollerProps) => {
  const scrollTopRef = useRef(0)
  const handleOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    scrollTopRef.current = scrollTop
  }

  const { scrollbarOptions, deps, ...rest } = props
  const scroller = useMemo(() => {
    return React.forwardRef((props: ScrollAreaProps, ref) => (
      <ScrollArea
        onScroll={handleOnScroll}
        ref={ref as RefCallback<HTMLDivElement>}
        scrollTop={scrollTopRef.current}
        scrollbarOptions={scrollbarOptions}
        {...rest}
        {...props}
      />
    )) as ComponentType<ScrollAreaProps>
  }, [deps])

  return scroller
}

/**
 * @description A custom scroller for ScrollingList and GroupedScrollingList.  Not intended to be used outside of these components.
 */
export const ScrollArea = forwardRef<BoxProps & ScrollAreaProps, 'div'>(
  (props: ScrollAreaProps, ref: ForwardedRef<HTMLDivElement>) => {
    const [mouseOver, setMouseOver] = React.useState(false)
    const { scrollbarOptions = {}, scrollTop, ...rest } = props
    const refObj =
      (ref as MutableRefObject<HTMLDivElement>) ?? useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!refObj.current || !scrollTop) {
        return
      }

      refObj.current.scrollTop = scrollTop
    }, [scrollTop])

    const handleMouseOver = (active: boolean) => {
      setMouseOver(active)
    }

    const getShouldHideScrollbar = () => {
      if (scrollbarOptions.showOnHoverOnly) {
        return !mouseOver
      }

      return Boolean(scrollbarOptions.hidden)
    }

    const shouldHideScrollbar = getShouldHideScrollbar()
    const className = shouldHideScrollbar ? 'scrollbar-hidden' : ''
    return (
      <Box
        ref={ref}
        onMouseEnter={() => handleMouseOver(true)}
        onMouseLeave={() => handleMouseOver(false)}
        className={className}
        {...rest}
        overflowY="scroll"
        overflowX="hidden"
        position="relative"
      >
        <Flex
          direction="column"
          flex={1}
          w="calc(100% - 6px)"
          position="relative"
        >
          {props.children}
        </Flex>
      </Box>
    )
  },
)
